import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import { Typography, Tooltip, Breadcrumbs, IconButton, Box, Alert, Snackbar } from '@mui/material';
import MUIDataTable from 'mui-datatables';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { AlertTriangle } from 'lucide-react';
import swal from 'sweetalert';
import Page from '../../../components/Page';
import PageHeader from '../../../components/PageHeader';
import adminServices from '../../../services/adminServices';
import MailTemplateDrawer from './MailTemplateDrawer';

const readError = (response, fallback) => response?.data?.message || fallback;

export default function MailTemplates() {
  const classes = useStyles();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchKey, setSearchKey] = useState('');
  const [sortKey, setSortKey] = useState('mail_template_type');
  const [sortOrder, setSortOrder] = useState('asc');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('view');
  const [drawerRecord, setDrawerRecord] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [drawerError, setDrawerError] = useState(null);

  const getTemplates = useCallback(async (pageNum, rows, sortOrd, sortCol, searchWord) => {
    setTemplates([]);
    setLoading(true);
    setListError(null);
    try {
      const response = await adminServices.getMailTemplates(pageNum, rows, searchWord, sortCol, sortOrd);
      if (response.ok) {
        setTemplates(response.data?.records || []);
        setPageCount(response.data?.metadata?.totalRecordCount || 0);
      } else {
        setListError(readError(response, 'Failed to load email templates.'));
      }
    } catch (error) {
      console.log(error, 'failed to [getMailTemplates]');
      setListError('Failed to load email templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getTemplates(1, rowsPerPage, sortOrder, sortKey, searchKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = () => getTemplates(page + 1, rowsPerPage, sortOrder, sortKey, searchKey);

  // The list response omits html_template (the bodies are large), so the drawer
  // always fetches the full record before opening in view/edit mode.
  const openDrawerFor = async (id, mode) => {
    setDrawerMode(mode);
    setDrawerRecord(null);
    setDrawerError(null);
    setDrawerOpen(true);
    setDrawerLoading(true);
    try {
      const response = await adminServices.getMailTemplateById(id);
      if (response.ok) {
        setDrawerRecord(response.data?.record || null);
      } else {
        setDrawerError(readError(response, 'Failed to load this template.'));
      }
    } catch (error) {
      console.log(error, 'failed to [getMailTemplateById]');
      setDrawerError('Failed to load this template.');
    } finally {
      setDrawerLoading(false);
    }
  };

  const openCreateDrawer = () => {
    setDrawerMode('create');
    setDrawerRecord(null);
    setDrawerError(null);
    setDrawerLoading(false);
    setDrawerOpen(true);
  };

  const handleSubmit = async (payload) => {
    setDrawerSaving(true);
    setDrawerError(null);
    try {
      const response =
        drawerMode === 'create'
          ? await adminServices.createMailTemplate(payload)
          : await adminServices.updateMailTemplate(drawerRecord.id, payload);

      if (response.ok) {
        setDrawerOpen(false);
        setToast(response.data?.message || 'Saved successfully.');
        reload();
      } else {
        setDrawerError(readError(response, 'Failed to save this template.'));
      }
    } catch (error) {
      console.log(error, 'failed to [saveMailTemplate]');
      setDrawerError('Failed to save this template.');
    } finally {
      setDrawerSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const confirmed = await swal({
      title: 'Delete this template?',
      text: `"${row.mail_template_type}" will be removed. Any API still sending with this type will stop finding a template.`,
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true,
    });
    if (!confirmed) return;

    try {
      const response = await adminServices.deleteMailTemplate(row.id);
      if (response.ok) {
        setToast(response.data?.message || 'Template deleted.');
        reload();
      } else {
        swal({ title: 'Could not delete', text: readError(response, 'Failed to delete this template.'), icon: 'error' });
      }
    } catch (error) {
      console.log(error, 'failed to [deleteMailTemplate]');
      swal({ title: 'Could not delete', text: 'Failed to delete this template.', icon: 'error' });
    }
  };

  const ellipsis = (value) =>
    value && value.length >= 30 ? (
      <Tooltip title={value}>
        <Typography className={classes.name}>{value}</Typography>
      </Tooltip>
    ) : (
      <Typography className={classes.name}>{value || '-'}</Typography>
    );

  const columns = [
    { name: 'id', label: 'Id', options: { filter: false, sort: false, display: false } },
    {
      name: 'mail_template_type',
      label: 'Template Type',
      options: { filter: false, sort: true, customBodyRender: ellipsis },
    },
    {
      name: 'template_name',
      label: 'Template Name',
      options: { filter: false, sort: true, customBodyRender: ellipsis },
    },
    {
      name: 'subject',
      label: 'Subject',
      options: { filter: false, sort: true, customBodyRender: ellipsis },
    },
    {
      name: 'place_holder_list',
      label: 'Placeholders',
      options: { filter: false, sort: false, customBodyRender: ellipsis },
    },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRenderLite: (dataIndex) => {
          const row = templates[dataIndex];
          if (!row) return null;
          return (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="View">
                <IconButton size="small" onClick={() => openDrawerFor(row.id, 'view')}>
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => openDrawerFor(row.id, 'edit')}>
                  <ModeEditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    },
  ];

  const Breadcrumb = () => (
    <Breadcrumbs aria-label="breadcrumb" separator="›">
      <Typography sx={{ textDecoration: 'none' }} variant="body2" color="primary" component={RouterLink} to="/app/dashboard">
        Dashboard
      </Typography>
      <Typography variant="body2" color="textPrimary">
        Email Templates
      </Typography>
    </Breadcrumbs>
  );

  return (
    <Page title="Email Templates">
      {/* Page's Container is padding:0 and the dashboard Outlet adds none either,
          so gutters come from the page - same wrapper the Plans & Pricing page uses. */}
      <div className="px-4 md:px-6 pt-4 pb-12 min-h-screen bg-gray-50">
        <PageHeader
          pageTitle="Email Templates"
          subTitle="Transactional and engagement emails sent by the platform."
          breadcrumbs={<Breadcrumb />}
          buttonText="Add Template"
          handleButtonClick={openCreateDrawer}
          onBack={() => navigate('/app/dashboard')}
        />

        {listError && (
          <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl">
            <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
            <p className="text-sm text-red-600">{listError}</p>
            <button type="button" onClick={reload} className="ml-auto text-xs font-semibold text-red-700 bg-transparent border-0 cursor-pointer">
              Retry
            </button>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden [&_.MuiPaper-root]:shadow-none [&_.MuiPaper-root]:rounded-none [&_.MuiPaper-root]:bg-transparent">
          <MUIDataTable
            data={templates}
            columns={columns}
            options={{
              pagination: true,
              filter: false,
              responsive: 'scroll',
              print: false,
              jumpToPage: true,
              download: false,
              page,
              search: true,
              count: pageCount,
              serverSide: true,
              rowsPerPage,
              rowsPerPageOptions: [10, 25, 50, 75, 100],
              selectableRows: false,
              textLabels: {
                body: { noMatch: loading ? 'loading' : 'sorry, no matching records found' },
                pagination: { jumpToPage: 'jumpToPage', rowsPerPage: 'rowsPerPage' },
              },
              onTableChange: (action, tableState) => {
                if (!['changeRowsPerPage', 'changePage', 'search', 'sort'].includes(action)) {
                  return;
                }
                const nextPage = action === 'sort' ? tableState.page : tableState.page + 1;
                const nextSortOrder = tableState.sortOrder.direction !== undefined ? tableState.sortOrder.direction : '';
                const nextSortKey = tableState.sortOrder.name !== undefined ? tableState.sortOrder.name : '';
                const nextSearchKey = tableState.searchText !== null ? tableState.searchText : '';

                setRowsPerPage(tableState.rowsPerPage);
                setTemplates([]);
                setPage(tableState.page);
                setSortKey(nextSortKey);
                setSearchKey(nextSearchKey);
                setSortOrder(nextSortOrder);
                getTemplates(nextPage, tableState.rowsPerPage, nextSortOrder, nextSortKey, nextSearchKey);
              },
            }}
              />
        </div>
      </div>

      <MailTemplateDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={drawerRecord}
        loading={drawerLoading}
        saving={drawerSaving}
        error={drawerError}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setToast(null)}>
          {toast}
        </Alert>
      </Snackbar>
    </Page>
  );
}

const useStyles = makeStyles((theme) => ({
  name: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: '220px',
    [theme.breakpoints.down('md')]: {
      maxWidth: 'unset',
    },
  },
}));
